import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ProductService } from '@/ui/_apiUrls';
import Button from '../Button';
import logo from '@/assets/image/logo.png';

interface DeleteProductDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  product: Record<string, any> | null;
  onDeleteSuccess?: () => void;
}

export default function DeleteProductDrawer({ isOpen, onClose, product, onDeleteSuccess }: DeleteProductDrawerProps) {
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);

  const { mutate: deleteProduct } = ProductService.delete({
    onSuccess: () => {
      setIsDeleting(false);
      queryClient.invalidateQueries({ queryKey: ['products'] });

      if (onDeleteSuccess) onDeleteSuccess();

      setTimeout(() => {
        onClose();
      }, 300);
    },
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isDeleting) {
      handleClose();
    }
  };

  const handleDelete = () => {
    if (!product) return;
    setIsDeleting(true);
    // eslint-disable-next-line no-underscore-dangle
    deleteProduct(product.id);
  };

  if (!product) return null;

  return (
    <>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed inset-0 z-50 bg-black bg-opacity-50"
            onClick={handleBackdropClick}
          />

          {/* Drawer */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
              duration: 0.5,
            }}
            className="fixed bottom-0 left-0 right-0 z-50 flex justify-center"
          >
            <div className="mx-0 flex max-h-[90vh] w-full max-w-[768px] flex-col overflow-hidden rounded-tl-[28px] rounded-tr-[28px] border border-zinc-300 bg-white shadow-lg sm:mx-4">
              {/* Handle */}
              <div className="flex w-full items-center justify-center px-4 pt-6">
                <button
                  type="button"
                  onClick={handleClose}
                  className="h-1.5 w-[52px] cursor-pointer rounded-[20px] bg-zinc-300 transition-colors duration-200 hover:bg-zinc-400"
                  aria-label="Close drawer"
                />
              </div>

              {/* Content */}
              <div className="flex flex-col items-center justify-center p-6">
                <div className="w-full max-w-md space-y-6">
                  {/* Product Info */}
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                      {product?.thumbnail ? (
                        <img src={product.thumbnail} alt={product.name} className="h-full w-full object-cover" />
                      ) : (
                        <img src={logo} className="h-full w-full object-contain p-2" alt="logo" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-lg font-semibold text-gray-900">
                        {product?.name || 'Untitled Product'}
                      </h3>
                      <p className="truncate text-sm text-gray-500">{product?.url || 'No URL provided'}</p>
                    </div>
                  </div>

                  {/* Confirmation Message */}
                  <div className="text-center">
                    <p className="text-base font-medium text-[#333333]">Delete this highlight?</p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex w-full gap-3">
                    <Button
                      variant={isDeleting ? 'ghost' : 'secondary'}
                      className="align-center h-12 max-w-none flex-1 rounded-lg border-2 border-sky-500 bg-white !p-0 !px-5 !py-3 font-medium text-sky-500 transition-colors duration-200 hover:bg-sky-50"
                      onClick={handleDelete}
                      disabled={isDeleting}
                    >
                      <p className="text-center font-roboto text-sm font-medium text-[#039BE5]">
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </p>
                    </Button>
                    <Button
                      variant="primary"
                      className="align-center h-12 max-w-none flex-1 rounded-lg bg-sky-500 !p-0 !px-5 !py-3 font-medium text-white transition-colors duration-200 hover:bg-sky-600"
                      onClick={handleClose}
                      disabled={isDeleting}
                    >
                      <p className="text-center font-roboto text-sm font-medium text-white">Cancel</p>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </>
  );
}
